import React from "react";
import { Instagram, Mail, ArrowUpRight } from "lucide-react";
import { LOGO_URL, CONTACT_EMAIL, INSTAGRAM_URL, INSTAGRAM_HANDLE, CALENDLY_URL, NAV_LINKS } from "@/lib/site-data";

const Footer = () => {
  const year = new Date().getFullYear();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-brand-blue text-brand-cream pt-20 pb-10 overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-periwinkle/20 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          {/* Brand block */}
          <div className="lg:col-span-6">
            <h2 className="font-serif font-light text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Let's build{" "}
              <span className="italic text-brand-periwinkle">together.</span>
            </h2>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-book-call-btn"
              className="mt-8 inline-flex items-center gap-2 bg-brand-cream text-brand-blue hover:bg-brand-periwinkle hover:text-brand-ink transition-colors rounded-full px-7 py-3.5 font-sans font-medium group"
            >
              Book a Discovery Call
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-12 transition-transform" />
            </a>
          </div>

          {/* Links */}
          <div className="lg:col-span-3">
            <p className="text-xs tracking-[0.25em] uppercase text-brand-periwinkle mb-5">
              Explore
            </p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l.id)}
                    data-testid={`footer-link-${l.id}`}
                    className="text-brand-cream/85 hover:text-brand-cream hover:translate-x-1 inline-block transition-all"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div className="lg:col-span-3">
            <p className="text-xs tracking-[0.25em] uppercase text-brand-periwinkle mb-5">
              Connect
            </p>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2 text-brand-cream/85 hover:text-brand-cream group transition-all"
                >
                  <Mail className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  <span className="break-all">{CONTACT_EMAIL}</span>
                </a>
              </li>
              <li>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="inline-flex items-center gap-2 text-brand-cream/85 hover:text-brand-cream group transition-all"
                >
                  <Instagram className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  @{INSTAGRAM_HANDLE}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-brand-cream/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="The Aligned SOULutions"
              className="h-10 w-10 rounded-lg object-contain bg-brand-cream p-0.5 ring-1 ring-brand-cream/25"
            />
            <span className="font-serif text-base">
              The Aligned <span className="italic text-brand-periwinkle">SOULutions</span>
            </span>
          </div>
          <p className="text-xs text-brand-cream/60 font-sans">
            © {year} The Aligned SOULutions. All rights reserved. · People-centered. Real results. Rooted in purpose.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
